import { useEffect, useState } from "react";
import { RiCheckboxCircleFill, RiErrorWarningFill, RiFileDownloadLine, RiInformationLine } from "react-icons/ri";
import { Link, useSearchParams } from "react-router-dom";

import ErrorIconSvg from "@/assets/svg/error-icon.svg?react";
import Combobox from "@/components/combobox";
import LabelledCombobox from "@/components/combobox/LabelledCombobox";
import Loader from "@/components/Loader";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import Table from "@/components/Table";
import Tooltip from "@/components/Tooltip";
import { STATUS_PLR } from "@/constants";
import api from "@/services/api";
import { captureError } from "@/services/error";
import { compactMissionFilters, searchMissions } from "@/services/mission";
import exportCSV from "@/utils/csv";

const TABLE_HEADER = [
  { title: "Mission", key: "title.keyword", width: "35%" },
  { title: "Places disponibles", key: "places" },
  { title: "Ville", key: "city.keyword", width: "20%" },
  { title: "Créée le", key: "createdAt" },
  { title: "Traitement", position: "center" },
  { title: "Statut", key: "statusCode.keyword" },
];

const TREATMENT_STATUS_CONFIG = {
  processed: {
    Icon: RiCheckboxCircleFill,
    className: "text-success",
    label: "Mission enrichie et scorée",
  },
  enriched_not_scored: {
    Icon: RiErrorWarningFill,
    className: "text-warning",
    label: "Mission enrichie, scoring manquant",
  },
  not_enriched: {
    Icon: RiErrorWarningFill,
    className: "text-warning",
    label: "Mission non enrichie, scoring manquant",
  },
};

const AdminMission = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    size: 25,
    page: Number(searchParams.get("page")) || 1,
    sortBy: "createdAt",
    publisherIds: searchParams.get("publisherIds") ? searchParams.get("publisherIds").split(",") : [],
    status: searchParams.get("status") || null,
    domain: searchParams.get("domain") || null,
    activity: searchParams.get("activity") || null,
    department: searchParams.get("department") || null,
    city: searchParams.get("city") || null,
    organization: searchParams.get("organization") || null,
    enrichmentScoringStatus: searchParams.get("enrichmentScoringStatus") || null,
    search: searchParams.get("search") || "",
  });
  const [options, setOptions] = useState({
    status: [],
    partners: [],
    domains: [],
    activities: [],
    departments: [],
    cities: [],
    organizations: [],
  });
  const [lastImport, setLastImport] = useState();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.post("/import/search", { size: 1 });
        if (!res.ok) {
          throw res;
        }
        setLastImport(res.data.length ? res.data[0] : null);
      } catch (error) {
        captureError(error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const newSearchParams = new URLSearchParams();
        Object.entries(compactMissionFilters(filters)).forEach(([key, value]) => newSearchParams.set(key, value));
        setSearchParams(newSearchParams);

        const res = await searchMissions(filters);

        if (!res.ok) {
          throw res;
        }
        setData(res.data);
        setOptions(res.aggs);
        setTotal(res.total);
      } catch (error) {
        captureError(error, { extra: { filters } });
      }
      setLoading(false);
    };

    fetchData();
  }, [filters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await searchMissions({ ...filters, size: total, page: 1 });

      if (!res.ok) {
        throw res;
      }

      const data = [];
      res.data.forEach((mission) => {
        const d = {};
        d["Id"] = mission._id;
        d["Partenaire Id"] = mission.publisherId;
        d["Partenaire"] = mission.publisherName;
        d["Client Id"] = mission.clientId;
        d["Titre"] = mission.title;
        d["Place"] = mission.places;
        d["Description"] = `"${mission.description.replace(/"/g, "'")}"`;
        d["Organisation"] = mission.organizationName;
        d["Ville"] = `${mission.city} - ${mission.country}`;
        d["Domaine"] = mission.domain;
        d["Activité"] = (mission.activities || []).join(", ");
        d["Statut"] = mission.statusCode;
        d["Commentaire statut"] = mission.statusComment;
        d["Créée le"] = new Date(mission.createdAt).toLocaleDateString("fr");
        d["Modifiée le"] = new Date(mission.updatedAt).toLocaleDateString("fr");
        d["Publiée le"] = new Date(mission.postedAt).toLocaleDateString("fr");
        data.push(d);
      });
      exportCSV("missions", data);
    } catch (error) {
      captureError(error, { extra: { filters } });
    }
    setExporting(false);
  };

  return (
    <div className="space-y-12 bg-white p-4 shadow-lg sm:p-12">
      <title>API Engagement - Missions - Administration</title>
      <div className="space-y-4">
        <SearchInput className="w-full sm:w-96" value={filters.search} onChange={(search) => setFilters({ ...filters, search })} placeholder="Rechercher par mot-clé" />
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
          <Select
            options={options.status.map((e) => ({ value: e.key, label: STATUS_PLR[e.key], count: e.doc_count }))}
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.value })}
            placeholder="Statut"
          />
          <Select
            options={options.domains.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : e.key, count: e.doc_count }))}
            value={filters.domain}
            onChange={(e) => setFilters({ ...filters, domain: e.value })}
            placeholder="Domaine"
          />
          <Select
            options={options.activities.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : e.key, count: e.doc_count }))}
            value={filters.activity}
            onChange={(e) => setFilters({ ...filters, activity: e.value })}
            placeholder="Activité"
          />
          {/* <Combobox
            id="publisher"
            options={options.partners.map((e) => ({ value: e.id, label: e.name, count: e.count }))}
            value={filters.publisherId}
            onSelect={(e) => (e ? setFilters({ ...filters, publisherId: e.value }) : null)}
            placeholder="Partenaire"
          /> */}
          <LabelledCombobox
            id="publisherIds"
            values={filters.publisherIds}
            onChange={(publisherIds) => setFilters({ ...filters, publisherIds })}
            placeholder="Partenaire"
            options={options.partners}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
          <Select
            options={options.cities.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : e.key, count: e.doc_count }))}
            value={filters.city}
            onChange={(e) => setFilters({ ...filters, city: e.value })}
            placeholder="Ville"
          />
          <Combobox
            id="department"
            options={options.departments.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : e.key, count: e.doc_count }))}
            value={filters.department}
            onSelect={(e) => (e ? setFilters({ ...filters, department: e.value }) : null)}
            placeholder="Département"
          />
          <Select
            options={options.organizations.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : e.key, count: e.doc_count }))}
            value={filters.organization}
            onChange={(e) => setFilters({ ...filters, organization: e.value })}
            placeholder="Organisation"
          />
          <Select
            options={[
              { value: "processed", label: "Enrichies + scorées" },
              { value: "enriched_not_scored", label: "Enrichies non scorées" },
              { value: "not_enriched", label: "Non enrichies" },
            ]}
            value={filters.enrichmentScoringStatus}
            onChange={(e) => setFilters({ ...filters, enrichmentScoringStatus: e.value })}
            placeholder="Traitement"
          />
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <h1 className="text-2xl font-bold">{total.toLocaleString("fr")} missions partagées</h1>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-text-mention text-base">Dernière synchronisation le {lastImport ? new Date(lastImport.startedAt).toLocaleDateString("fr") : "N/A"}</p>
              {lastImport && new Date(lastImport.startedAt) > new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 1) ? (
                <RiCheckboxCircleFill role="img" aria-label="Synchronisation à jour" className="text-success text-base" />
              ) : (
                <ErrorIconSvg role="img" aria-label="Synchronisation en retard" className="fill-error h-4 w-4" />
              )}
            </div>
          </div>
          <button className="tertiary-btn flex shrink-0 items-center" onClick={handleExport}>
            {exporting ? <Loader /> : <RiFileDownloadLine className="mr-2" aria-hidden="true" />}
            Exporter
          </button>
        </div>

        <Table
          caption="Liste des missions"
          header={TABLE_HEADER}
          pagination
          page={filters.page}
          pageSize={filters.size}
          onPageChange={(page) => setFilters({ ...filters, page })}
          total={total}
          loading={loading}
          sortBy={filters.sortBy}
          onSort={(sortBy) => setFilters({ ...filters, sortBy })}
        >
          {data.map((item, i) => (
            <tr key={i} className={`${i % 2 === 0 ? "bg-table-even" : "bg-table-odd"} table-row`}>
              <td className="p-4">
                <Link to={`/mission/${item._id}`} className="text-blue-france line-clamp-3">
                  {item.title}
                </Link>
                {item.publisherName && <p className="text-sm">{item.publisherName}</p>}
                {item.organizationName && <p className="text-sm">{item.organizationName}</p>}
              </td>
              <td className="px-4">{item.places}</td>
              <td className="px-4">{item.city}</td>
              <td className="px-4">{new Date(item.createdAt).toLocaleDateString("fr")}</td>
              <td className="px-4 text-center">
                {(() => {
                  const treatmentStatus = TREATMENT_STATUS_CONFIG[item.adminEnrichmentScoringStatus] ?? TREATMENT_STATUS_CONFIG.not_enriched;
                  const TreatmentIcon = treatmentStatus.Icon;
                  return (
                    <Tooltip
                      ariaLabel={treatmentStatus.label}
                      triggerClassName={`${treatmentStatus.className} inline-flex justify-center`}
                      tooltipClassName="border-grey-border border bg-white p-4 text-sm shadow-lg"
                      content={treatmentStatus.label}
                    >
                      <TreatmentIcon role="img" aria-label={treatmentStatus.label} className={`${treatmentStatus.className} text-2xl`} />
                    </Tooltip>
                  );
                })()}
              </td>
              <td className="px-6">
                <div className="flex items-center gap-1">
                  {item.statusCode === "ACCEPTED" ? (
                    <RiCheckboxCircleFill role="img" aria-label="Acceptée par l'API" className="text-success text-2xl" />
                  ) : (
                    <ErrorIconSvg role="img" aria-label="Non acceptée par l'API" className="fill-error h-6 w-6" />
                  )}
                  {item.statusComment ? (
                    <Tooltip
                      ariaLabel="Voir le commentaire de statut"
                      triggerClassName="text-text-mention"
                      tooltipClassName="border-grey-border w-64 border bg-white p-4 text-sm shadow-lg"
                      content={item.statusComment}
                    >
                      <RiInformationLine className="text-2xl" aria-hidden="true" />
                    </Tooltip>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </div>
  );
};

export default AdminMission;
